import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { HeartPulse, PenLine, CheckCircle2, XCircle, Loader2, ShieldCheck, CreditCard, Smartphone, FileDown, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, API, fmtXof, fmtDate, apiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MM_OPERATORS = ["Orange Money", "MTN MoMo", "Wave"];

function AcompteSection({ devis, token, onPaid }) {
  const [busy, setBusy] = useState("");
  const [momoOpen, setMomoOpen] = useState(false);
  const [momo, setMomo] = useState({ operator: "Orange Money", phone: "" });
  const amount = Math.round(devis.totals.ttc_afrique * 0.30);
  const pdfUrl = `${API}/devis/sign/${token}/pdf`;

  const payStripe = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post("/devis/acompte/stripe", { token, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Paiement impossible"));
      setBusy("");
    }
  };

  const payMomo = async () => {
    setBusy("momo");
    try {
      const { data } = await api.post("/devis/acompte/momo", { token, operator: momo.operator, phone: momo.phone });
      setMomoOpen(false);
      onPaid(data.amount, `Mobile Money ${momo.operator}`);
    } catch (e) {
      toast.error(apiError(e, "Paiement impossible"));
    } finally { setBusy(""); }
  };

  if (devis.acompte?.paid_at) {
    return (
      <div className="mt-8 rounded-2xl border border-emerald-300 bg-emerald-50 p-6" data-testid="acompte-paid">
        <p className="flex items-center gap-2 font-display text-base font-semibold text-emerald-700">
          <BadgeCheck size={18} /> Acompte de démarrage payé
        </p>
        <p className="mt-2 text-sm text-emerald-700">
          {fmtXof(devis.acompte.amount)} reçus le {fmtDate(devis.acompte.paid_at)} ({devis.acompte.method}).
          Le Cabinet CARIS Santé démarre votre prestation.
        </p>
        <a href={pdfUrl} data-testid="acompte-pdf-link"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-light">
          <FileDown size={14} /> Télécharger le devis signé (PDF)
        </a>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-2xl border border-ciel/30 bg-white p-6 shadow-sm" data-testid="acompte-section">
      <p className="font-mono text-xs uppercase tracking-widest text-ciel">Démarrage de la prestation</p>
      <h2 className="mt-2 font-display text-xl font-bold text-navy">Acompte de 30 % — {fmtXof(amount)}</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Conformément aux conditions générales, le démarrage est conditionné au versement de l'acompte.
        Solde à la livraison : {fmtXof(devis.totals.ttc_afrique - amount)}.
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Button data-testid="acompte-stripe-btn" onClick={payStripe} disabled={!!busy}
          className="rounded-full bg-navy py-6 text-sm font-semibold text-white hover:bg-navy-light">
          {busy === "stripe" ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={16} className="mr-2" /> Payer par carte bancaire</>}
        </Button>
        <Button data-testid="acompte-momo-btn" onClick={() => setMomoOpen(true)} disabled={!!busy} variant="outline"
          className="rounded-full border-2 border-orange-400 py-6 text-sm font-semibold text-orange-600 hover:bg-orange-50">
          <Smartphone size={16} className="mr-2" /> Payer par Mobile Money
        </Button>
      </div>
      <a href={pdfUrl} data-testid="signed-pdf-link"
        className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-ciel hover:text-navy">
        <FileDown size={13} /> Télécharger le devis signé (PDF)
      </a>

      <Dialog open={momoOpen} onOpenChange={setMomoOpen}>
        <DialogContent data-testid="acompte-momo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Acompte Mobile Money — {fmtXof(amount)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={momo.operator} onValueChange={(v) => setMomo({ ...momo, operator: v })}>
              <SelectTrigger data-testid="acompte-momo-operator"><SelectValue /></SelectTrigger>
              <SelectContent>{MM_OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="acompte-momo-phone" placeholder="Numéro Mobile Money (ex : +225 01 02 03 04 05)" value={momo.phone}
              onChange={(e) => setMomo({ ...momo, phone: e.target.value })} />
            <Button data-testid="acompte-momo-confirm" onClick={payMomo} disabled={busy === "momo"}
              className="w-full rounded-full bg-orange-500 py-5 text-sm font-semibold text-white hover:bg-orange-600">
              {busy === "momo" ? <Loader2 className="animate-spin" size={15} /> : `Confirmer le paiement de ${fmtXof(amount)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SoldeSection({ devis, token, onPaid }) {
  const [busy, setBusy] = useState("");
  const [momoOpen, setMomoOpen] = useState(false);
  const [momo, setMomo] = useState({ operator: "Orange Money", phone: "" });
  const amount = devis.totals.ttc_afrique - devis.acompte.amount;
  const pdfUrl = `${API}/devis/sign/${token}/pdf`;

  const payStripe = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post("/devis/solde/stripe", { token, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Paiement impossible"));
      setBusy("");
    }
  };

  const payMomo = async () => {
    setBusy("momo");
    try {
      const { data } = await api.post("/devis/solde/momo", { token, operator: momo.operator, phone: momo.phone });
      setMomoOpen(false);
      onPaid(data.amount, `Mobile Money ${momo.operator}`);
    } catch (e) {
      toast.error(apiError(e, "Paiement impossible"));
    } finally { setBusy(""); }
  };

  if (devis.solde?.paid_at) {
    const invoiceUrl = `${API}/devis/sign/${token}/facture`;
    return (
      <div className="mt-8 rounded-2xl border border-emerald-400 bg-emerald-100 p-6" data-testid="solde-paid">
        <p className="flex items-center gap-2 font-display text-base font-semibold text-emerald-800">
          <BadgeCheck size={18} /> Devis intégralement soldé
        </p>
        <p className="mt-2 text-sm text-emerald-700">
          Solde de {fmtXof(devis.solde.amount)} reçu le {fmtDate(devis.solde.paid_at)} ({devis.solde.method}).
          Merci pour votre confiance — Cabinet CARIS Santé.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={invoiceUrl} data-testid="invoice-pdf-link"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-2.5 text-xs font-semibold text-white hover:bg-emerald-800">
            <FileDown size={14} /> Télécharger la facture acquittée (PDF)
          </a>
          <a href={pdfUrl} data-testid="solde-pdf-link"
            className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy-light">
            <FileDown size={14} /> Télécharger le devis soldé (PDF)
          </a>
        </div>
      </div>
    );
  }

  if (!devis.delivered_at) {
    return (
      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6" data-testid="solde-pending">
        <p className="font-display text-base font-semibold text-navy">Solde restant — {fmtXof(amount)} (70 %)</p>
        <p className="mt-2 text-sm text-slate-500">
          Votre prestation est en cours de réalisation. Le solde sera à régler ici après la livraison —
          vous serez notifié par email.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50/60 p-6 shadow-sm" data-testid="solde-section">
      <p className="font-mono text-xs uppercase tracking-widest text-amber-600">Prestation livrée</p>
      <h2 className="mt-2 font-display text-xl font-bold text-navy">Solde restant — {fmtXof(amount)} (70 %)</h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        Votre prestation a été livrée. Il reste à régler le solde pour finaliser le devis {devis.number}.
      </p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Button data-testid="solde-stripe-btn" onClick={payStripe} disabled={!!busy}
          className="rounded-full bg-navy py-6 text-sm font-semibold text-white hover:bg-navy-light">
          {busy === "stripe" ? <Loader2 className="animate-spin" size={16} /> : <><CreditCard size={16} className="mr-2" /> Payer par carte bancaire</>}
        </Button>
        <Button data-testid="solde-momo-btn" onClick={() => setMomoOpen(true)} disabled={!!busy} variant="outline"
          className="rounded-full border-2 border-orange-400 py-6 text-sm font-semibold text-orange-600 hover:bg-orange-50">
          <Smartphone size={16} className="mr-2" /> Payer par Mobile Money
        </Button>
      </div>

      <Dialog open={momoOpen} onOpenChange={setMomoOpen}>
        <DialogContent data-testid="solde-momo-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Solde Mobile Money — {fmtXof(amount)}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={momo.operator} onValueChange={(v) => setMomo({ ...momo, operator: v })}>
              <SelectTrigger data-testid="solde-momo-operator"><SelectValue /></SelectTrigger>
              <SelectContent>{MM_OPERATORS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="solde-momo-phone" placeholder="Numéro Mobile Money (ex : +225 01 02 03 04 05)" value={momo.phone}
              onChange={(e) => setMomo({ ...momo, phone: e.target.value })} />
            <Button data-testid="solde-momo-confirm" onClick={payMomo} disabled={busy === "momo"}
              className="w-full rounded-full bg-orange-500 py-5 text-sm font-semibold text-white hover:bg-orange-600">
              {busy === "momo" ? <Loader2 className="animate-spin" size={15} /> : `Confirmer le paiement de ${fmtXof(amount)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function DevisSign() {
  const { token } = useParams();
  const [devis, setDevis] = useState(null);
  const [error, setError] = useState("");
  const [acceptCg, setAcceptCg] = useState(false);
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmRefuse, setConfirmRefuse] = useState(false);
  const [done, setDone] = useState("");
  const [justPaid, setJustPaid] = useState(null);

  const load = () => api.get(`/devis/sign/${token}`)
    .then(({ data }) => setDevis(data))
    .catch((e) => setError(apiError(e, "Lien de signature invalide")));

  useEffect(() => { load(); }, [token]);

  // Retour de Stripe : vérification du paiement (acompte ou solde)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const acompteSession = params.get("acompte_session");
    const soldeSession = params.get("solde_session");
    const sessionId = acompteSession || soldeSession;
    if (!sessionId) return;
    const path = acompteSession ? `/devis/acompte/status/${sessionId}` : `/devis/solde/status/${sessionId}`;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(path);
        if (data.payment_status === "paid") {
          toast.success("Paiement reçu — merci !");
          await load();
          return;
        }
      } catch { /* silencieux */ }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2500);
    };
    poll();
  }, [token]);

  const sign = async (action) => {
    if (!signature.trim() || signature.trim().length < 2) {
      toast.error("Saisissez votre nom complet pour signer");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/devis/sign/${token}`, { action, signature_name: signature.trim() });
      setDone(action);
      if (action === "accepte") await load();
    } catch (e) {
      toast.error(apiError(e, "Signature impossible"));
    } finally {
      setBusy(false);
      setConfirmRefuse(false);
    }
  };

  const markPaid = async (amount, method) => {
    setJustPaid({ amount, method });
    await load();
  };

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="sign-error">
        <XCircle size={48} className="mx-auto text-red-400" />
        <h1 className="mt-5 font-display text-2xl font-bold text-navy">Lien invalide</h1>
        <p className="mt-2 text-sm text-slate-500">{error}</p>
        <Link to="/" className="mt-6 inline-block rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white">Retour à la boutique</Link>
      </div>
    );
  }

  if (!devis) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" /></div>;
  }

  if (done === "refuse") {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center" data-testid="sign-success">
        <XCircle size={56} className="mx-auto text-red-400" />
        <h1 className="mt-6 font-display text-3xl font-bold text-navy">Devis refusé</h1>
        <p className="mt-3 text-slate-600">Le devis {devis.number} a été marqué comme refusé. Le Cabinet CARIS Santé reste à votre disposition.</p>
        <Link to="/" className="mt-8 inline-block rounded-full bg-ciel px-7 py-3.5 text-sm font-semibold text-white hover:bg-sky-700">Retour à la boutique</Link>
      </div>
    );
  }

  const decided = devis.status === "accepte" || devis.status === "refuse";
  const t = devis.totals;

  return (
    <div className="mx-auto max-w-4xl px-6 py-14" data-testid="devis-sign-page">
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-navy text-white"><HeartPulse size={26} /></span>
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-ciel">Cabinet CARIS Santé — Abidjan, Côte d'Ivoire</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-navy">Devis commercial {devis.number}</h1>
          <p className="mt-2 text-sm text-slate-500">
            Émis le {fmtDate(devis.created_at)} · Valable jusqu'au {devis.expires_at ? fmtDate(devis.expires_at) : "—"}
          </p>
        </div>
      </div>

      {done === "accepte" && (
        <div className="mt-8 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700" data-testid="sign-just-signed">
          <CheckCircle2 size={16} className="mr-2 inline" /> Merci {signature} — le devis {devis.number} est accepté et signé.
        </div>
      )}

      {decided && !done && (
        <div className={`mt-8 rounded-xl border p-4 text-sm font-semibold ${devis.status === "accepte" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-600"}`} data-testid="sign-status-banner">
          Ce devis a déjà été {devis.status === "accepte" ? "accepté" : "refusé"}
          {devis.signed_by ? ` par ${devis.signed_by}` : ""}{devis.signed_at ? ` le ${fmtDate(devis.signed_at)}` : ""}.
        </div>
      )}

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6">
        <p className="text-sm font-semibold text-navy">{devis.civility} {devis.name}</p>
        <p className="text-xs text-slate-500">{[devis.institution, devis.city, devis.country].filter(Boolean).join(" · ")}</p>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="sign-items">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left font-mono text-[10px] uppercase tracking-wider text-slate-400">
              <th className="p-4">Réf.</th><th className="p-4">Désignation</th><th className="p-4">Délai</th>
              <th className="p-4 text-right">Qté</th><th className="p-4 text-right">Prix unitaire HT</th><th className="p-4 text-right">Total HT</th>
            </tr>
          </thead>
          <tbody>
            {devis.items.map((it, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="p-4 font-mono text-xs text-slate-500">{devis.number}-{String(i + 1).padStart(2, "0")}</td>
                <td className="p-4">
                  <p className="font-medium text-navy">{it.title}</p>
                  {it.maintenance && <p className="text-xs text-slate-400">+ Maintenance 1ère année (10 %)</p>}
                  {it.conditions && <p className="mt-1 text-xs italic text-slate-500">{it.conditions}</p>}
                </td>
                <td className="p-4 text-xs text-slate-600">{it.delay || "À définir"}</td>
                <td className="p-4 text-right font-mono text-xs">{it.qty || 1}</td>
                <td className="p-4 text-right font-mono text-xs">{fmtXof(it.price)}</td>
                <td className="p-4 text-right font-mono text-xs font-semibold text-navy">
                  {fmtXof(it.price * (it.qty || 1) * (it.maintenance ? 1.1 : 1))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-slate-200 p-6">
          <dl className="ml-auto max-w-xs space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Total HT</dt><dd className="font-mono text-navy">{fmtXof(t.total_ht)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">TVA 18 % (UEMOA)</dt><dd className="font-mono text-slate-600">{fmtXof(t.tva_afrique)}</dd></div>
            <div className="flex justify-between border-t border-slate-100 pt-2"><dt className="font-semibold text-navy">Total TTC</dt><dd className="font-mono font-bold text-navy" data-testid="sign-ttc">{fmtXof(t.ttc_afrique)}</dd></div>
            <div className="flex justify-between text-xs"><dt className="text-slate-400">International (TVA 20 %)</dt><dd className="font-mono text-slate-500">{fmtXof(t.ttc_intl)}</dd></div>
          </dl>
        </div>
      </div>

      {devis.observations && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Observations</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{devis.observations}</p>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5 text-xs leading-relaxed text-slate-500">
        <strong className="text-slate-600">Conditions générales :</strong> ce devis est valable 30 jours.
        Tout démarrage de prestation est conditionné à la signature du devis et au versement d'un acompte de 30 %.
        Solde à la livraison. Les tarifs internationaux sont donnés à titre indicatif selon le taux de change du jour.
      </div>

      {!decided && (
        <div className="mt-8 rounded-2xl border border-ciel/30 bg-sky-50/50 p-6" data-testid="sign-form">
          <p className="flex items-center gap-2 font-display text-base font-semibold text-navy">
            <PenLine size={17} className="text-ciel" /> Signature électronique
          </p>
          <label className="mt-4 flex items-start gap-2 text-sm text-slate-600">
            <Checkbox checked={acceptCg} onCheckedChange={(v) => setAcceptCg(!!v)} data-testid="sign-accept-cg" className="mt-0.5" />
            J'ai lu et j'accepte les conditions générales du devis {devis.number}
          </label>
          <div className="mt-4">
            <label className="text-xs font-semibold text-slate-600">Nom complet (tenu lieu de signature)</label>
            <Input data-testid="sign-name" className="mt-1.5 bg-white" placeholder="Ex : Awa Koné" value={signature}
              onChange={(e) => setSignature(e.target.value)} />
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button data-testid="sign-accept-btn" disabled={!acceptCg || busy} onClick={() => sign("accepte")}
              className="flex-1 rounded-full bg-emerald-600 py-5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <><ShieldCheck size={15} className="mr-1.5" /> Accepter et signer le devis</>}
            </Button>
            <Button data-testid="sign-refuse-btn" variant="outline" disabled={busy} onClick={() => setConfirmRefuse(true)}
              className="rounded-full py-5 text-red-500 hover:text-red-600">
              Refuser le devis
            </Button>
          </div>
        </div>
      )}

      {justPaid && !devis.acompte?.paid_at && !devis.solde?.paid_at && (
        <div className="mt-8 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700" data-testid="acompte-just-paid">
          <CheckCircle2 size={16} className="mr-2 inline" /> Acompte de {fmtXof(justPaid.amount)} payé par {justPaid.method}. Merci !
        </div>
      )}

      {devis.status === "accepte" && <AcompteSection devis={devis} token={token} onPaid={markPaid} />}
      {devis.status === "accepte" && devis.acompte?.paid_at && <SoldeSection devis={devis} token={token} onPaid={markPaid} />}

      <Dialog open={confirmRefuse} onOpenChange={setConfirmRefuse}>
        <DialogContent data-testid="sign-refuse-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">Refuser le devis {devis.number} ?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Cette décision sera transmise au Cabinet CARIS Santé. Saisissez votre nom complet pour confirmer.</p>
          <div className="mt-3 flex gap-3">
            <Button variant="outline" onClick={() => setConfirmRefuse(false)} className="flex-1 rounded-full">Annuler</Button>
            <Button data-testid="sign-refuse-confirm" disabled={!signature.trim() || busy} onClick={() => sign("refuse")}
              className="flex-1 rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Confirmer le refus"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
